import { Button, Space, Tooltip, Popconfirm } from "antd";


import {
  canCreateProject,
  canEditProject,
  getCurrentUserRole,
  parseRole,
  Role,
} from "../../config/rbac";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import "../../styles/trackon.css";

export const normalizeRole = (value) =>
  (value || "").trim().toUpperCase();

export const isProjectManagerRole = (role) =>
  parseRole(role) === Role.PROJECT_MANAGER;

export const canDeleteProjects = (role) => {
  const parsedRole = parseRole(role);

  return [Role.ADMIN, Role.PMO, Role.ACCOUNT_MANAGER].includes(parsedRole);
};

export const canManageProjects = (role) => {
  return canCreateProject(role) || canEditProject(role, { isAssignedProject: true });
};

export const getSessionRole = () => getCurrentUserRole();

export function filterProjects(
  projects,
  filters,
) {
  const {
    search,
    statusFilter,
    flagFilter,
    deliveryManager,
    dateRange,
  } = filters;

  return projects.filter((project) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      project.projectId.toLowerCase().includes(q) ||
      project.projectName.toLowerCase().includes(q) ||
      project.customerName.toLowerCase().includes(q) ||
      project.managerName.toLowerCase().includes(q);

    const matchStatus =
      statusFilter === "all" || project.status === statusFilter;

    const matchFlag =
      flagFilter === "all" ||
      (flagFilter === "flagged" && project.redFlagged) ||
      (flagFilter === "clean" && !project.redFlagged);

    const matchManager =
      deliveryManager === "all" ||
      (project.deliveryManager || "")
        .toLowerCase()
        .includes(deliveryManager);

    const matchDateRange = dateRange === "all";

    return (
      matchSearch &&
      matchStatus &&
      matchFlag &&
      matchManager &&
      matchDateRange
    );
  });
}



export function getProjectColumns(
  handlers,
) {

  const headerCellStyle = {
    background: "#eef2f8",
    color: "#2d3a5e",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "1px solid #dbe3ef",
    whiteSpace: "nowrap",
    textAlign: "left",
    verticalAlign: "middle",
  };
  const bodyCellStyle = {
    fontSize: 12,
    color: "#5c6070",
    textAlign: "left",
    verticalAlign: "middle",
  };
  const onHeaderCell = () => ({ style: headerCellStyle });
  const onCell = () => ({ style: bodyCellStyle });

  const sortableHeader = (col) => () => ({
    style: {
      ...headerCellStyle,
      cursor: "pointer",
      userSelect: "none",
    },
    onClick: () => handlers.onSort?.(col),
  });
  const sortTitle = (label, col) => {
    const active = handlers.sortCol === col;
    return (
      <>
        {label}
        <span
          style={{
            marginLeft: 3,
            opacity: active ? 0.9 : 0.35,
            color: active ? "#1e40af" : "inherit",
            fontSize: 10,
          }}
        >
          {active ? (handlers.sortDir === 1 ? "↑" : "↓") : "↕"}
        </span>
      </>
    );
  };

  return [
    {
      title: sortTitle("Project ID", "projectId"),
      dataIndex: "projectId",
      width: "14%",
      align: "left",
      onHeaderCell: sortableHeader("projectId"),
      onCell,
      render: (value) => (
        <span style={{fontSize: 12 }}>
          {value}
        </span>
      ),
    },
    {
      title: sortTitle("Project Name", "projectName"),
      dataIndex: "projectName",
      width: "24%",
      align: "left",
      onHeaderCell: sortableHeader("projectName"),
      onCell: () => ({
        style: {
          ...bodyCellStyle,
          whiteSpace: "normal",
          wordBreak: "break-word",
          fontWeight: 600,
          color: "#3c3c3e",
          fontSize: 13,
        },
      }),
    },
    {
      title: sortTitle("Customer Name", "customerName"),
      dataIndex: "customerName",
      width: "24%",
      align: "left",
      onHeaderCell: sortableHeader("customerName"),
      onCell: () => ({
        style: {
          ...bodyCellStyle,
          whiteSpace: "normal",
          wordBreak: "break-word",
        } ,
      }),
    },
    {
      title: sortTitle("Contract Category", "contractCategory"),
      dataIndex: "contractCategory",
      width: "16%",
      align: "left",
      onHeaderCell: sortableHeader("contractCategory"),
      onCell: () => ({
        style: {
          ...bodyCellStyle,
          whiteSpace: "normal",
          wordBreak: "break-word",
        },
      }),
    },
    {
      title: sortTitle("Start Date", "startDate"),
      dataIndex: "startDate",
      width: "10%",
      align: "left",
      onHeaderCell: sortableHeader("startDate"),
      onCell,
    },
    {
      title: sortTitle("End Date", "endDate"),
      dataIndex: "endDate",
      width: "10%",
      align: "left",
      onHeaderCell: sortableHeader("endDate"),
      onCell,
    },
    {
      title: "",
      key: "actions",
      width: "8%",
      align: "center",
      onHeaderCell,
      render: (_, row) => {
        const editDisabled =
          handlers.canEdit === false || handlers.canEditRow?.(row) === false;

        const deleteDisabled =
          handlers.canDelete === false ||
          handlers.canDeleteRow?.(row) === false;

        const isDeleting = handlers.deletingId === row.id;

        return (
          <Space size={4}>
            {canDeleteProjects(getCurrentUserRole()) && (
            <Tooltip title="Edit project">
              <Button
                icon={<EditOutlinedIcon style={{ fontSize: 15 }} />}
                type="text"
                size="small"
                disabled={editDisabled}
                style={{ fontSize: 16 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handlers.onEdit(row.id);
                }}
              />
            </Tooltip>
            )}

            {!deleteDisabled && (
              <Popconfirm
              overlayClassName="trackon-root"
                title="Delete project"
                description="Are you sure you want to delete this project? This action cannot be undone."
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true, loading: isDeleting }}
                onConfirm={(e) => {
                  e?.stopPropagation?.();
                  handlers.onDelete?.(row);
                }}
                onCancel={(e) => e?.stopPropagation?.()}
              >
                <Tooltip title="Delete project">
                  <Button
                    icon={
                      <DeleteOutlineOutlinedIcon style={{ fontSize: 15 }} />
                    }
                    type="text"
                    size="small"
                    danger
                    loading={isDeleting}
                    style={{ fontSize: 16 }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];
}